/**
 *  @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Icon, Input, Form, Label as BaseLabel } from 'semantic-ui-react';
import { map, cloneDeep } from 'lodash';
import { format } from 'date-fns';

import { Modal, Button, Table, Toggle } from '../../../components';
import RemoveInvoiceButton from './RemoveInvoiceButton';
import PaymentTypeField from '../../appointments/components/details/invoice/PaymentTypeField';
import PaymentTypeFieldMembership from '../../appointments/components/details/invoice/PaymentTypeFieldMembership';
import { GET_REPORT_QUERY } from '../containers/seller/SellerReportTable';
import { prepareFloatForServer } from '../../../lib/numberFormatter';

const EDIT_INVOICE_QUERY = gql`
    mutation editInvoice($input: EditInvoiceInput!) {
        editInvoice(input: $input) {
            invoice {
                id
                items {
                    id
                    price
                    paymentType
                }
            }
        }
    }
`;

const ARCHIVE_PRODUCTS_MUTATION = gql`
    mutation archiveCartInvoice($input: ArchiveCartInvoiceInput!) {
        archiveCartInvoice(input: $input) {
            invoice {
                id
                archived
            }
        }
    }
`;

const DATE_FORMAT = 'yyyy-MM-dd';

const intlMessages = defineMessages({
    walkinClient: {
        id: 'pages.appointments.details.AppointmentDetails.walkinClient',
        defaultMessage: 'Walk-in Client',
    },
    itemPriceError: {
        id: 'pages.report.editInvoice.priceError',
        defaultMessage: 'Enter price',
    },
});

const TableRow = ({ children }) => (
    <Table.Row style={{ position: 'relative' }}>{children}</Table.Row>
);

const ClientCell = ({ client, intl }) => {
    let txt = '';
    if (client && client.firstName) {
        txt = `${client.firstName} ${client.lastName}`;
        if (client.phone) txt += ` (${client.phone})`;
    } else {
        txt = intl.formatMessage(intlMessages.walkinClient);
    }
    return <Table.Cell>{txt}</Table.Cell>;
};

function EditInvoiceDialog({
    trigger, open, onClose,
    client, item, invoiceId, invoiceType, archived,
    intl,
}) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const fromDate = useSelector(({ report }) => format(new Date(report.get('fromDate')), DATE_FORMAT));
    const toDate = useSelector(({ report }) => format(new Date(report.get('toDate')), DATE_FORMAT));
    const showArchive = useSelector(({ salon: s }) => s.get('showArchive'));
    const reportFilter = useSelector(({ report }) => report.get('reportFilter'));
    const currentLanguage = useSelector(({ intl: i }) => i.get('locale') || i.get('defaultLanguage'));

    const queryVariables = { salon, fromDate, toDate, deleted: true, showArchive, reportFilter, lang: currentLanguage };

    const methods = useForm();
    const { control, handleSubmit, reset, formState: { isSubmitting, isDirty } } = methods;

    useEffect(() => {
        if (open && item) {
            reset({
                price: parseFloat(item.price).toFixed(2),
                paymentType: item.paymentType,
            });
        }
    }, [open, item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const [editInvoice] = useMutation(EDIT_INVOICE_QUERY, {
        update: (store) => {
            try {
                const data = store.readQuery({ query: GET_REPORT_QUERY, variables: queryVariables });
                store.writeQuery({ query: GET_REPORT_QUERY, variables: queryVariables, data });
            } catch (e) {
                // safe to ignore
            }
        },
    });

    const [archiveInvoice] = useMutation(ARCHIVE_PRODUCTS_MUTATION, {
        update: (store, { data: { archiveCartInvoice: { invoice } } }) => {
            try {
                const data = store.readQuery({ query: GET_REPORT_QUERY, variables: queryVariables });
                const updateArchive = i => {
                    const newItem = cloneDeep(i);
                    if (newItem.invoiceId === invoice.id) newItem.archived = invoice.archived;
                    return newItem;
                };
                const updatedData = {
                    ...data,
                    viewer: {
                        ...data.viewer,
                        salon: {
                            ...data.viewer.salon,
                            administrator: {
                                ...data.viewer.salon.administrator,
                                report: {
                                    ...data.viewer.salon.administrator.report,
                                    invoices: map(data.viewer.salon.administrator.report.invoices, updateArchive),
                                },
                            },
                            administrators: map(data.viewer.salon.administrators, admin => ({
                                ...admin,
                                report: { ...admin.report, invoices: map(admin.report.invoices, updateArchive) },
                            })),
                            masters: {
                                ...data.viewer.salon.masters,
                                nodes: map(data.viewer.salon.masters.nodes, master => ({
                                    ...master,
                                    report: { ...master.report, invoices: map(master.report.invoices, updateArchive) },
                                })),
                            },
                        },
                    },
                };
                store.writeQuery({ query: GET_REPORT_QUERY, variables: queryVariables, data: updatedData });
            } catch (e) {
                // safe to ignore
            }
        },
    });

    const onSubmit = async formData => {
        await editInvoice({
            variables: {
                input: {
                    invoiceId,
                    itemId: item.id,
                    price: prepareFloatForServer(formData.price),
                    paymentType: formData.paymentType,
                },
            },
        });
        onClose();
    };

    const onArchive = async (e, id) => {
        await archiveInvoice({ variables: { input: { invoiceId: id, archived: e } } });
        onClose();
    };

    const onModalClose = () => {
        reset();
        onClose();
    };

    return (
        <FormProvider {...methods}>
            <Modal size="large" trigger={trigger} open={open} onClose={onModalClose}>
                <Modal.Header>
                    <FormattedMessage id="pages.report.editInvoice.edit" defaultMessage="Edit invoice" />
                </Modal.Header>
                <Modal.Content>
                    <Form>
                        <Table celled fixed>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>
                                        <FormattedMessage id="pages.report.ReportTable.client" defaultMessage="Client" />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell>
                                        <FormattedMessage id="pages.report.ReportTable.service" defaultMessage="Service/Product" />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell style={{ width: '120px' }}>
                                        <FormattedMessage id="pages.report.ReportTable.price" defaultMessage="Price" />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell style={{ width: '220px' }}>
                                        <FormattedMessage id="pages.report.ReportTable.paymentType" defaultMessage="Payment type" />
                                    </Table.HeaderCell>
                                    {invoiceType === 'CART' && (
                                        <Table.HeaderCell style={{ width: '145px' }}>
                                            <FormattedMessage id="pages.report.ReportTable.archive" defaultMessage="Unarchive/Archive" />
                                        </Table.HeaderCell>
                                    )}
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                <TableRow>
                                    <ClientCell client={client} intl={intl} />
                                    <Table.Cell>{item.entity.name}</Table.Cell>
                                    <Table.Cell style={{ width: '120px' }}>
                                        <Controller
                                            name="price"
                                            control={control}
                                            rules={{
                                                validate: value =>
                                                    !value || value.match(/[^,.\d]/)
                                                        ? intl.formatMessage(intlMessages.itemPriceError)
                                                        : true,
                                            }}
                                            render={({ field, fieldState }) => (
                                                <>
                                                    <Input
                                                        {...field}
                                                        value={field.value ?? ''}
                                                        fluid
                                                    />
                                                    {fieldState.error && (
                                                        <BaseLabel color="red" pointing basic>{fieldState.error.message}</BaseLabel>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Table.Cell>
                                    {/* overflow:visible lets the payment-type
                                        dropdown menu expand past the cell — the
                                        `fixed` table otherwise sets
                                        overflow:hidden on every td and clips it. */}
                                    <Table.Cell
                                        style={{
                                            width: '220px',
                                            overflow: 'visible',
                                        }}
                                    >
                                        <Controller
                                            name="paymentType"
                                            control={control}
                                            render={({ field: { ref: _ref, value, onChange } }) => {
                                                const Component = item.paymentType === 'MEMBERSHIP'
                                                    ? PaymentTypeFieldMembership
                                                    : PaymentTypeField;
                                                return (
                                                    <Component
                                                        value={value}
                                                        onChange={(e, data) => onChange(data?.value ?? e)}
                                                        fluid
                                                    />
                                                );
                                            }}
                                        />
                                    </Table.Cell>
                                    {invoiceType === 'CART' && (
                                        <Table.Cell textAlign="center" style={{ width: '145px' }}>
                                            <Toggle
                                                onChange={e => onArchive(e, invoiceId)}
                                                value={archived}
                                            />
                                        </Table.Cell>
                                    )}
                                </TableRow>
                            </Table.Body>
                        </Table>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        onClick={handleSubmit(onSubmit)}
                        primary
                    >
                        <Icon name="checkmark" />
                        <FormattedMessage id="pages.settings.manufacturers.form.edit.save" defaultMessage="Save" />
                    </Button>
                    <Button disabled={isSubmitting} onClick={onModalClose} basic>
                        <Icon name="remove" />
                        <FormattedMessage id="pages.settings.manufacturers.form.edit.cancel" defaultMessage="Cancel" />
                    </Button>
                    <RemoveInvoiceButton invoiceId={invoiceId} />
                </Modal.Actions>
            </Modal>
        </FormProvider>
    );
}

export default injectIntl(EditInvoiceDialog);
